'use strict';

define('vb/private/vx/nativeMobileExtensionAdapter',['vb/private/vx/remoteExtensionAdapter', 'vb/binding/expression', 'vb/private/utils', 'vb/private/constants',
  'vb/private/mobile/fileIo'],
(RemoteExtensionAdapter, Expression, Utils, Constants, FileIo) => {
  const EXT_MANIFEST_FILE_NAME = 'manifest.json';

  /**
   * The extension adapter used to load extension from local directory that is cached from extension manager
   */
  class NativeMobileExtensionAdapter extends RemoteExtensionAdapter {
    /**
     * Return a promise to load the vx manifest from data directory of a mobile application
     * @return {Promise}  a promise that resolves to the manifest
     * @private
     */
    static readManifestFromDataDirectory() {
      return FileIo.readFileFromLib(Constants.EXTENSION_PATH, EXT_MANIFEST_FILE_NAME)
        .then((fileContent) => JSON.parse(fileContent));
    }

    /**
     * Takes a vx manifest file and writes it to the data directory of
     * @param manifest {Object}
     * @private
     */
    static writeManifestToDataDirectory(manifest) {
      return FileIo.saveFileToLib(Constants.EXTENSION_PATH_NAME, EXT_MANIFEST_FILE_NAME, JSON.stringify(manifest))
        .then(() => manifest);
    }

    /**
     * compares the downloaded extensions on the device with the one from server and builds an array
     * of extension that need downloading
     * @param {Array<Object>} deviceExtensions extensions that currently exist on the device
     * @param {Array<Object>} serverExtensions extensions from the new manifest read from the server
     * @return {Array<Object>} array of extension that need downloading to the device
     * @private
     */
    static buildExtensionToDownload(deviceExtensions, serverExtensions) {
      const extensionsToDownload = [];
      // start with the server side manifest extensions so that we do not miss any new ones
      serverExtensions.forEach((extn) => {
        const extnVersion = extn.version || '';

        // always download development extension so that we can overwrite the cached one
        if (extnVersion.startsWith('$')
            // check for id or version mismatch
            || deviceExtensions.filter((deviceExtn) => (deviceExtn.id === extn.id
                                                                && deviceExtn.version === extn.version)).length === 0) {
          extensionsToDownload.push(extn);
        }
      });

      return extensionsToDownload;
    }

    /**
     * Return a promise to load the manifest given the extensionId and the baseUrl of
     * the extension manager.
     * @param  {String} extensionId the extension id
     * @return {Promise} a promise that resolves to the manifest
     * @override
     */
    loadManifest(extensionId) {
      // first load the manifest from the data directory
      return NativeMobileExtensionAdapter.readManifestFromDataDirectory()
        .then((dataDirManifest) => {
          // If we are offline or if the registryUrl is not set because of explicit downloads,
          // use cached version of the manifest from the data directory
          if (Utils.isOnline() && this.registryUrl) {
            // load from server and compare
            return super.loadManifest(extensionId)
              .then((manifest) => {
                // if we have previously loaded and computed the extensions to download, we don't recompute. We will
                // reuse them
                if (!this.extensionsToDownload) {
                  this.extensionsToDownload = NativeMobileExtensionAdapter
                    .buildExtensionToDownload(dataDirManifest.extensions, manifest.extensions);
                }

                return manifest;
              })
              .catch((err) => {
                // we are online, but the server may be offline, so use cached stuff on the device
                this.log.error('Error loading extension registry from server, using offline version', err);
                return dataDirManifest;
              });
          }
          return dataDirManifest;
        })
        // manifest load failed from data directory. Load from server and cache
        .catch(() => {
          // If there is no registry url return an empty manifest
          if (!this.registryUrl) {
            return {};
          }
          return super.loadManifest(extensionId)
            .then((manifest) => {
              // download all the extensions
              this.extensionsToDownload = manifest.extensions;
              return manifest;
            });
        });
    }

    /**
     * returns a list of extensions that need to be downloaded. The list is computed when loadManifest is invoked
     *
     * @return {Promise<Array>}
     * @see loadManifest
     */
    getExtensionsToDownload() {
      return Promise.resolve(this.extensionsToDownload);
    }

    /**
     * Internal method to update the progressVariable Expression when extensions are being downloaded
     * @param {String} progressUpdateActionId ID of action in current chain to execute for providing
     *  progress update during extension downloads
     * @param {ActionChain} actionChain the action chain under which 'progressUpdateActionId' action is invoked
     * @param {Object} actionContext available context for running the action 'progressUpdateActionId'
     * @param {Number} percent the percent of progress during download
     * @private
     */
    static runProgressUpdateActionStep(progressUpdateActionId = null, actionChain, actionContext, percent) {
      if (progressUpdateActionId) {
        // eslint-disable-next-line no-param-reassign
        actionContext[Constants.ContextName.CURRENT] = { percent };
        return actionChain.runActionStep(progressUpdateActionId, actionContext);
      }

      return Promise.resolve();
    }

    /**
     * Caches extensions to the data directory for native mobile applications
     *
     * @param {Object} manifest a manifest containing an array of extensions
     * @param {String} progressUpdateActionId ID of action in current chain to execute for providing
     *  progress update during extension downloads
     * @param {ActionChain} actionChain the action chain under which 'progressUpdateActionId' action is invoked
     * @param {Object} actionContext available context for running the action 'progressUpdateActionId'
     * @return {Promise<Object>} returns an object with the following properties
     *   manifest - the original manifest and
     *   cachedExtensions - the list of cached extensions
     * @override
     */
    cacheExtensions(manifest, progressUpdateActionId = null, actionChain, actionContext) {
      const downloadedExtensions = this.extensionsToDownload;
      // use our previously computed extension delta array to trigger download to the device
      if (downloadedExtensions && downloadedExtensions.length > 0) {
        // set the progress update to 0
        return NativeMobileExtensionAdapter.runProgressUpdateActionStep(progressUpdateActionId,
          actionChain, actionContext, 0).then(() => this
          .downloadExtensionsToDevice(downloadedExtensions, progressUpdateActionId, actionChain, actionContext)
          // write the updated manifest only after downloading extensions to maintain data integrity
          .then(() => NativeMobileExtensionAdapter.writeManifestToDataDirectory(manifest))
          .then(() => {
            delete this.extensionsToDownload;

            // set the progress update to 100%
            return NativeMobileExtensionAdapter.runProgressUpdateActionStep(progressUpdateActionId,
              actionChain, actionContext, 100).then(() => {
              const result = { manifest, cachedExtension: downloadedExtensions };
              return result;
            });
          }));
      }
      return super.cacheExtensions(manifest);
    }

    /**
     *
     * @param  {Array} extensions array of extensions
     * @param {String} progressUpdateActionId ID of action in current chain to execute for providing
     *  progress update during extension downloads
     * @param {ActionChain} actionChain the action chain under which 'progressUpdateActionId' action is invoked
     * @param {Object} actionContext available context for running the action 'progressUpdateActionId'
     * @return {Promise} a promise to an array of extension objects
     * @private
     */
    downloadExtensionsToDevice(extensions, progressUpdateActionId = null, actionChain, actionContext) {
      const promises = [];
      extensions.forEach((extension) => {
        const files = extension.files || [];
        const baseUrl = extension.ui && extension.ui.baseUrl;
        const extensionId = extension.id || '';

        if (baseUrl && extensionId) {
          const downloadPromise = new Promise((resolve) => {
            window.resolveLocalFileSystemURL(window.cordova.file.dataDirectory, (dataPathDir) => {
              const filePromises = [];
              files.forEach((filePath, index) => {
                const extPath = `${Constants.EXTENSION_PATH}${extensionId}${Constants.PATH_SEPARATOR}${filePath}`;
                const extIndex = filePath.lastIndexOf('.');
                const ext = extIndex !== -1 ? filePath.substr(extIndex + 1) : '';
                const request = new Request(`${baseUrl}/${filePath}`);
                const extensionLoadTimer = this.log.monitor('', request.url);
                const fetchPromise = fetch(request)
                  .then((response) => {
                    if (!response.ok) {
                      throw new Error('Response not OK when fetching file for extension at:', request.url);
                    }
                    // assume json response based on json extension
                    if (ext === 'json') {
                      return response.json();
                    }
                    if (/^(html|js|css)$/i.test(ext)) {
                      return response.text();
                    }
                    // all other case assume binary data
                    return response.blob();
                  })
                  .then((content) => FileIo.createFileContentOnDevice(dataPathDir, extPath, content)
                    // update the progress.
                    // Note that for multiple extensions, the progress will restart. We don't do a progress across
                    // all extensions which would require an additional loop to calculate total file count
                    .then(() => NativeMobileExtensionAdapter.runProgressUpdateActionStep(progressUpdateActionId,
                      actionChain, actionContext, (index * 100) / files.length)))
                  .then(() => this.log.info('Downloaded Extn', filePath, 'for', extensionId, extensionLoadTimer()))
                  .catch((e) => {
                    // don't reject the promise chain because of fetch fail for a single file. Do an error log
                    // and continue fetching other files
                    this.log.error('Error', e, 'downloading file', filePath, 'for extension', extensionId);
                  });
                filePromises.push(fetchPromise);
              });
              Promise.all(filePromises).then(() => resolve(files));
            });
          });
          promises.push(downloadPromise);
        }
      });

      return Promise.all(promises);
    }

    /**
     * Given an extension id and its base URL this function returns a mapping (object with properties/values)
     * to be used with requirejs like:
     * {
     *   'vx/manifestId': 'file://.../sources'
     * }
     * @param  {extensionId} the id of the extension
     * @param  {baseUrl} the base URL of the extension
     * @return {String} a mapping string
     * @override
     */
    // eslint-disable-next-line class-methods-use-this
    buildMapping(extensionId) {
      const mapping = {};

      const extnIdPath = `${Constants.EXTENSION_PATH}${extensionId}`;
      mapping[extnIdPath] = `${window.cordova.file.dataDirectory}${extnIdPath}`;

      return mapping;
    }
  }

  return NativeMobileExtensionAdapter;
});

